// CRM accent palette — brand-led (Muzukuru tan/brown) with vivid accents.
//
// Same policy as statusColors.ts: each accent is a self-contained
// {bg, fg, border} pill so it keeps its own contrast on ANY page background
// (light or dark). Use these for category/type chips, avatars, stat cards,
// section headers and tab indicators across the CRM (contacts, companies,
// sales). The soft `bg` values are low-alpha tints of the accent, so they read
// correctly over both light paper and dark surfaces.

export type CrmAccentKey =
  | "brand" //  warm brown — primary brand accent
  | "amber" //  tan/gold   — secondary brand accent
  | "teal" //   teal       — individuals / activity
  | "green" //  green      — success / complete / verified
  | "indigo" // indigo     — companies / organisations
  | "rose" //   rose       — highlights / attention
  | "slate"; //  neutral    — muted / metadata

export interface CrmAccent {
  /** Soft tinted background for chips / cards / icon wells. */
  bg: string;
  /** Foreground (text + icon) — high contrast against `bg`. */
  fg: string;
  /** Border / divider for the accent. */
  border: string;
  /** Solid fill (e.g. filled buttons, avatars) — pair with white text. */
  solid: string;
}

export type CrmAccentMap = Record<CrmAccentKey, CrmAccent>;

// fg values are the saturated/darkened accent so text stays legible on the
// soft tint; bg/border are alpha tints of the same hue so they sit well on any
// surface. solid is the full-strength accent for filled elements.
const CRM_ACCENTS: CrmAccentMap = {
  brand: { bg: "#724E411F", fg: "#5D3F35", border: "#724E4147", solid: "#724E41" },
  amber: { bg: "#C58A4E26", fg: "#9A5B1E", border: "#C58A4E4D", solid: "#C58A4E" },
  teal: { bg: "#0D94881F", fg: "#0B7E73", border: "#0D948840", solid: "#0D9488" },
  green: { bg: "#2E9E5B1F", fg: "#1E7D45", border: "#2E9E5B40", solid: "#2E9E5B" },
  indigo: { bg: "#6870FA1F", fg: "#4B53D6", border: "#6870FA40", solid: "#6870FA" },
  rose: { bg: "#E5557A1F", fg: "#C13860", border: "#E5557A40", solid: "#E5557A" },
  slate: { bg: "#64748B1F", fg: "#475569", border: "#64748B40", solid: "#64748B" },
};

export const getCrmAccent = (key: CrmAccentKey): CrmAccent => CRM_ACCENTS[key];

export const getCrmAccents = (): CrmAccentMap => CRM_ACCENTS;

// Ordered palette used to deterministically color avatars/categories by index
// or by hashing a string key (so the same contact always gets the same color).
export const CRM_ACCENT_CYCLE: CrmAccentKey[] = [
  "teal",
  "indigo",
  "amber",
  "green",
  "rose",
  "brand",
];

/** Stable accent for an arbitrary string (name, category, source). */
export const accentForKey = (key: string | null | undefined): CrmAccent => {
  const s = key || "";
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  }
  return CRM_ACCENTS[CRM_ACCENT_CYCLE[hash % CRM_ACCENT_CYCLE.length]];
};

/** Company vs individual get fixed, recognisable colors. */
export const accentForContactType = (
  type: "company" | "individual" | string | null | undefined,
): CrmAccent => (type === "company" ? CRM_ACCENTS.indigo : CRM_ACCENTS.teal);
